/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './pre-op-anaesthesia-chart.reducer';

export const PreOpAnaesthesiaChart = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const preOpAnaesthesiaChartList = useAppSelector(state => state.preOpAnaesthesiaChart.entities);
  const loading = useAppSelector(state => state.preOpAnaesthesiaChart.loading);
  const totalItems = useAppSelector(state => state.preOpAnaesthesiaChart.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="pre-op-anaesthesia-chart-heading" data-cy="PreOpAnaesthesiaChartHeading">
        <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.home.title">Pre Op Anaesthesia Charts</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/pre-op-anaesthesia-chart/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.home.createLabel">Create new Pre Op Anaesthesia Chart</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.preOpAnaesthesiaChart.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {preOpAnaesthesiaChartList && preOpAnaesthesiaChartList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('typeOfAnaesthesia')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.typeOfAnaesthesia">Type Of Anaesthesia</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('typeOfAnaesthesia')} />
                </th>
                <th className="hand" onClick={sort('generalCondition')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.generalCondition">General Condition</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('generalCondition')} />
                </th>
                <th className="hand" onClick={sort('ivAccess')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ivAccess">Iv Access</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ivAccess')} />
                </th>
                <th className="hand" onClick={sort('site')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.site">Site</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('site')} />
                </th>
                <th className="hand" onClick={sort('cannulaGuage')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.cannulaGuage">Cannula Guage</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cannulaGuage')} />
                </th>
                <th className="hand" onClick={sort('raTechnique')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raTechnique">Ra Technique</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raTechnique')} />
                </th>
                <th className="hand" onClick={sort('raApproach')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raApproach">Ra Approach</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raApproach')} />
                </th>
                <th className="hand" onClick={sort('raNeedle')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raNeedle">Ra Needle</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raNeedle')} />
                </th>
                <th className="hand" onClick={sort('raSpace')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raSpace">Ra Space</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raSpace')} />
                </th>
                <th className="hand" onClick={sort('raDrug')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raDrug">Ra Drug</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raDrug')} />
                </th>
                <th className="hand" onClick={sort('raOnsetAction')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raOnsetAction">Ra Onset Action</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raOnsetAction')} />
                </th>
                <th className="hand" onClick={sort('raSensoryLevel')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raSensoryLevel">Ra Sensory Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raSensoryLevel')} />
                </th>
                <th className="hand" onClick={sort('raMotorLevel')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.raMotorLevel">Ra Motor Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('raMotorLevel')} />
                </th>
                <th className="hand" onClick={sort('gaPreOxygenation')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.gaPreOxygenation">Ga Pre Oxygenation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('gaPreOxygenation')} />
                </th>
                <th className="hand" onClick={sort('induction')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.induction">Induction</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('induction')} />
                </th>
                <th className="hand" onClick={sort('inhalational')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.inhalational">Inhalational</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('inhalational')} />
                </th>
                <th className="hand" onClick={sort('etube')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.etube">Etube</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('etube')} />
                </th>
                <th className="hand" onClick={sort('etSize')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.etSize">Et Size</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('etSize')} />
                </th>
                <th className="hand" onClick={sort('etCuff')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.etCuff">Et Cuff</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('etCuff')} />
                </th>
                <th className="hand" onClick={sort('etOtherInfo')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.etOtherInfo">Et Other Info</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('etOtherInfo')} />
                </th>
                <th className="hand" onClick={sort('ventilation')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ventilation">Ventilation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ventilation')} />
                </th>
                <th className="hand" onClick={sort('spontaneous')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.spontaneous">Spontaneous</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('spontaneous')} />
                </th>
                <th className="hand" onClick={sort('ventilatorMv')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ventilatorMv">Ventilator Mv</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ventilatorMv')} />
                </th>
                <th className="hand" onClick={sort('ventilatorRr')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ventilatorRr">Ventilator Rr</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ventilatorRr')} />
                </th>
                <th className="hand" onClick={sort('ventilatorTv')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ventilatorTv">Ventilator Tv</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ventilatorTv')} />
                </th>
                <th className="hand" onClick={sort('ventilatorIvRatio')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.ventilatorIvRatio">Ventilator Iv Ratio</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ventilatorIvRatio')} />
                </th>
                <th className="hand" onClick={sort('patientPosition')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.patientPosition">Patient Position</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientPosition')} />
                </th>
                <th className="hand" onClick={sort('torniquetAppliedAt')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.torniquetAppliedAt">Torniquet Applied At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('torniquetAppliedAt')} />
                </th>
                <th className="hand" onClick={sort('torniquetRemovedAt')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.torniquetRemovedAt">Torniquet Removed At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('torniquetRemovedAt')} />
                </th>
                <th className="hand" onClick={sort('timeOfInduction')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.timeOfInduction">Time Of Induction</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfInduction')} />
                </th>
                <th className="hand" onClick={sort('timeOfIndcision')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.timeOfIndcision">Time Of Indcision</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfIndcision')} />
                </th>
                <th className="hand" onClick={sort('timeOfSurgeyCompletion')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.timeOfSurgeyCompletion">
                    Time Of Surgey Completion
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfSurgeyCompletion')} />
                </th>
                <th className="hand" onClick={sort('timeOfExtubation')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.timeOfExtubation">Time Of Extubation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timeOfExtubation')} />
                </th>
                <th className="hand" onClick={sort('additionalNotes')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.additionalNotes">Additional Notes</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('additionalNotes')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.surgery">Surgery</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.anaesthesiologist">Anaesthesiologist</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.vitals">Vitals</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.nurse">Nurse</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {preOpAnaesthesiaChartList.map((preOpAnaesthesiaChart, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/pre-op-anaesthesia-chart/${preOpAnaesthesiaChart.id}`} color="link" size="sm">
                      {preOpAnaesthesiaChart.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.TypeOfAnaesthesia.${preOpAnaesthesiaChart.typeOfAnaesthesia}`} />
                  </td>
                  <td>{preOpAnaesthesiaChart.generalCondition}</td>
                  <td>{preOpAnaesthesiaChart.ivAccess}</td>
                  <td>{preOpAnaesthesiaChart.site}</td>
                  <td>{preOpAnaesthesiaChart.cannulaGuage}</td>
                  <td>{preOpAnaesthesiaChart.raTechnique}</td>
                  <td>{preOpAnaesthesiaChart.raApproach}</td>
                  <td>{preOpAnaesthesiaChart.raNeedle}</td>
                  <td>{preOpAnaesthesiaChart.raSpace}</td>
                  <td>{preOpAnaesthesiaChart.raDrug}</td>
                  <td>{preOpAnaesthesiaChart.raOnsetAction}</td>
                  <td>{preOpAnaesthesiaChart.raSensoryLevel}</td>
                  <td>{preOpAnaesthesiaChart.raMotorLevel}</td>
                  <td>{preOpAnaesthesiaChart.gaPreOxygenation}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.Induction.${preOpAnaesthesiaChart.induction}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.OPInhalational.${preOpAnaesthesiaChart.inhalational}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.OPEtube.${preOpAnaesthesiaChart.etube}`} />
                  </td>
                  <td>{preOpAnaesthesiaChart.etSize}</td>
                  <td>{preOpAnaesthesiaChart.etCuff}</td>
                  <td>{preOpAnaesthesiaChart.etOtherInfo}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.OPVentilation.${preOpAnaesthesiaChart.ventilation}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.Spontaneous.${preOpAnaesthesiaChart.spontaneous}`} />
                  </td>
                  <td>{preOpAnaesthesiaChart.ventilatorMv}</td>
                  <td>{preOpAnaesthesiaChart.ventilatorRr}</td>
                  <td>{preOpAnaesthesiaChart.ventilatorTv}</td>
                  <td>{preOpAnaesthesiaChart.ventilatorIvRatio}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientPosition.${preOpAnaesthesiaChart.patientPosition}`} />
                  </td>
                  <td>{preOpAnaesthesiaChart.torniquetAppliedAt}</td>
                  <td>{preOpAnaesthesiaChart.torniquetRemovedAt}</td>
                  <td>{preOpAnaesthesiaChart.timeOfInduction}</td>
                  <td>{preOpAnaesthesiaChart.timeOfIndcision}</td>
                  <td>{preOpAnaesthesiaChart.timeOfSurgeyCompletion}</td>
                  <td>{preOpAnaesthesiaChart.timeOfExtubation}</td>
                  <td>{preOpAnaesthesiaChart.additionalNotes}</td>
                  <td>{preOpAnaesthesiaChart.active ? 'true' : 'false'}</td>
                  <td>{preOpAnaesthesiaChart.cursor}</td>
                  <td>{preOpAnaesthesiaChart.lastupdated}</td>
                  <td>{preOpAnaesthesiaChart.microSec}</td>
                  <td>{preOpAnaesthesiaChart.timestampentry}</td>
                  <td>
                    {preOpAnaesthesiaChart.surgery ? (
                      <Link to={`/patient-procedure/${preOpAnaesthesiaChart.surgery.id}`}>{preOpAnaesthesiaChart.surgery.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {preOpAnaesthesiaChart.anaesthesiologist ? (
                      <Link to={`/doctor/${preOpAnaesthesiaChart.anaesthesiologist.id}`}>{preOpAnaesthesiaChart.anaesthesiologist.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {preOpAnaesthesiaChart.vitals ? (
                      <Link to={`/vitals/${preOpAnaesthesiaChart.vitals.id}`}>{preOpAnaesthesiaChart.vitals.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {preOpAnaesthesiaChart.nurse ? (
                      <Link to={`/nurse/${preOpAnaesthesiaChart.nurse.id}`}>{preOpAnaesthesiaChart.nurse.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/pre-op-anaesthesia-chart/${preOpAnaesthesiaChart.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/pre-op-anaesthesia-chart/${preOpAnaesthesiaChart.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/pre-op-anaesthesia-chart/${preOpAnaesthesiaChart.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.preOpAnaesthesiaChart.home.notFound">No Pre Op Anaesthesia Charts found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={preOpAnaesthesiaChartList && preOpAnaesthesiaChartList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PreOpAnaesthesiaChart;
